import {
    RESET_GANTT,
    SET_LOCK_FILTER,
    SET_SELECTED_STOCK_REQUIREMENT,
    SET_RESCHEDULE_ORDER_UUID,
    SET_RESCHEDULE_LINE_UUID,
    SET_RESCHEDULE_ORDER_BUNDLE,
} from "../common";
import { TypeFilterValues } from "@/modules/shared/planningTable/typeFilterConsts";
import * as BusinessLogic from "@/lib/businessLogic";
import { deepClone } from "@/lib/util";

import type {
    ResetGanttAction,
    SetLockFilter,
    SetSelectedStockRequirement,
    SetRescheduleLineAction,
    SetRescheduleOrderAction,
    SetRescheduleOrderBundleAction,
} from "../common";
import type { PayloadAction } from "@/store/types";
import type {
    CustomFilterEntity,
    CustomFilterInputType,
    CustomFilterMatch,
} from "../../lineOrderList/filters/CustomFilter";

export type SELECTED_FILTER_TYPES = keyof typeof TypeFilterValues;

export const SET_SELECTED_LINE_UUIDS = "set_selected_line_uuids";
export const SET_SELECTED_MATERIALS = "set_selected_materials";
export const SET_ORDER_EXTERNAL_IDS = "order_external_ids";
export const SET_START_TIME = "set_start_time";
export const SET_INSIGHT_TYPE = "set_insight_type";
export const SET_PLANNING_RESULT_TYPE = "set_planning_result_type";

export const RESET_FILTERS = "reset_filters";
export const SET_SELECTED_FILTER_TYPE = "set_selected_filter_type";
export const SET_SELECTED_ORDER_EXTERNAL_IDS = "set_selected_order_external_ids";
export const SET_SELECTED_CUSTOM_FILTER_CONFIG = "set_selected_custom_filter_config";
export const SET_SELECTED_CUSTOM_FILTER_VALUES = "set_selected_custom_filter_values";

export type SelectedCustomFilterConfig = {
    selected_custom_filter_entity: CustomFilterEntity | null;
    selected_custom_filter_field: string | null;
    selected_custom_filter_input_type: CustomFilterInputType | null;
    selected_custom_filter_match: CustomFilterMatch | null;
};

export type SetSelectedLineUuidsAction = PayloadAction<typeof SET_SELECTED_LINE_UUIDS, string[]>;
export type SetSelectedMaterialAction = PayloadAction<typeof SET_SELECTED_MATERIALS, string[]>;
export type SetOrderExternalIdsAction = PayloadAction<typeof SET_ORDER_EXTERNAL_IDS, string[]>;
export type SetStartTimeAction = PayloadAction<typeof SET_START_TIME, number | null>;
export type SetInsightTypeAction = PayloadAction<typeof SET_INSIGHT_TYPE, string | null>;
export type SetPlanningResultTypeAction = PayloadAction<typeof SET_PLANNING_RESULT_TYPE, string | null>;
export type ResetFiltersAction = PayloadAction<typeof RESET_FILTERS, typeof undefined>;
export type SetSelectedFilterType = PayloadAction<typeof SET_SELECTED_FILTER_TYPE, SELECTED_FILTER_TYPES | null>;
export type SetSelectedOrderUuids = PayloadAction<typeof SET_SELECTED_ORDER_EXTERNAL_IDS, string[]>;
export type SetSelectedCustomFilterConfigAction = PayloadAction<
    typeof SET_SELECTED_CUSTOM_FILTER_CONFIG,
    SelectedCustomFilterConfig
>;
export type SetSelectedCustomFilterValuesAction = PayloadAction<typeof SET_SELECTED_CUSTOM_FILTER_VALUES, string[]>;

export type PlanningTableFiltersState = {
    // debugging
    is_dev_env: boolean;

    // healty part
    selected_filter_type: SELECTED_FILTER_TYPES | null;
    is_filter_locked: boolean;
    reschedule_line_uuid: string | null;
    reschedule_order_uuid: string | null;
    selected_line_uuids: string[];
    selected_order_external_ids: string[];
    selected_materials: string[];
    selected_order_tag: string | null;
    selected_order_tag_values: string[];
    selected_material_tag: string | null;
    selected_material_tag_values: string[];
    selected_insight_type: string | null;
    selected_planning_result_type: string | null;
    selected_filter_match: string | null;
    selected_filter_input_type: string | null;
    selected_custom_filter_entity: CustomFilterEntity | null;
    selected_custom_filter_field: string | null;
    selected_custom_filter_input_type: CustomFilterInputType | null;
    selected_custom_filter_match: CustomFilterMatch | null;
    selected_custom_filter_values: string[];

    // old part
    order_external_ids: string[];
    order_start_time: number | null;
};

export const filters_initial_state: PlanningTableFiltersState = {
    // debugging
    is_dev_env: BusinessLogic.getSysFlag("env") === "dev",

    // healty part
    selected_filter_type: null,
    is_filter_locked: false,
    reschedule_line_uuid: null,
    reschedule_order_uuid: null,
    selected_line_uuids: [],
    selected_order_external_ids: [],
    selected_materials: [],
    selected_order_tag: null,
    selected_order_tag_values: [],
    selected_material_tag: null,
    selected_material_tag_values: [],
    selected_insight_type: null,
    selected_planning_result_type: null,
    selected_filter_match: null,
    selected_filter_input_type: null,
    selected_custom_filter_entity: null,
    selected_custom_filter_field: null,
    selected_custom_filter_input_type: null,
    selected_custom_filter_match: null,
    selected_custom_filter_values: [],

    // old part
    order_external_ids: [],
    order_start_time: null,
};

export type PlanningTableFiltersActionTypes =
    | SetSelectedLineUuidsAction
    | SetSelectedMaterialAction
    | SetOrderExternalIdsAction
    | SetStartTimeAction
    | SetRescheduleLineAction
    | SetInsightTypeAction
    | SetPlanningResultTypeAction
    | ResetFiltersAction
    | SetRescheduleOrderAction
    | ResetGanttAction
    | SetLockFilter
    | SetSelectedFilterType
    | SetSelectedOrderUuids
    | SetSelectedStockRequirement
    | SetRescheduleOrderBundleAction
    | SetSelectedCustomFilterConfigAction
    | SetSelectedCustomFilterValuesAction;

const getInitialState = (state: PlanningTableFiltersState): PlanningTableFiltersState => {
    return deepClone({
        ...filters_initial_state,
        is_filter_locked: state ? state.is_filter_locked : false,
    });
};

export const planningTableFiltersReducer = (
    state: PlanningTableFiltersState,
    action: PlanningTableFiltersActionTypes
): PlanningTableFiltersState => {
    if (!state) {
        state = getInitialState(state);
    }
    let new_state = state;

    if (state.is_dev_env) {
        console.log("Redux type dispatch", action.type);
    }

    if (action.type === RESET_GANTT) {
        return getInitialState(state);
    } else if (action.type === RESET_FILTERS) {
        return getInitialState(state);
    } else if (action.type === SET_SELECTED_LINE_UUIDS) {
        new_state = getInitialState(state);
        new_state.selected_line_uuids = action.data;
        new_state.selected_filter_type =
            action.data.length === 0 && state.is_filter_locked ? null : TypeFilterValues.line;
        return { ...new_state };
    } else if (action.type === SET_SELECTED_MATERIALS) {
        new_state = getInitialState(state);
        new_state.selected_filter_type = TypeFilterValues.material;
        new_state.selected_materials = action.data;
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_LINE_UUID) {
        new_state = getInitialState(state);
        new_state.selected_filter_type = TypeFilterValues.reschedule_line;
        new_state.reschedule_line_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_ORDER_UUID) {
        new_state = getInitialState(state);
        new_state.selected_filter_type = TypeFilterValues.reschedule_operation;
        new_state.reschedule_order_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_ORDER_BUNDLE) {
        const state = new_state;
        const is_filter_locked = action.data.is_filter_locked;
        if (!is_filter_locked) {
            state.reschedule_order_uuid = action.data.order_uuid;
            state.selected_filter_type = TypeFilterValues.reschedule_operation;
            state.reschedule_line_uuid = null;
        }
        state.is_filter_locked = is_filter_locked;
        if (JSON.stringify(state) !== JSON.stringify(new_state)) {
            return { ...state };
        }
        return new_state;
    } else if (action.type === SET_ORDER_EXTERNAL_IDS) {
        new_state = getInitialState(state);
        new_state.order_external_ids = action.data;
        return { ...new_state };
    } else if (action.type === SET_START_TIME) {
        new_state.order_start_time = action.data;
        return { ...new_state };
    } else if (action.type === SET_INSIGHT_TYPE) {
        new_state.selected_insight_type = action.data;
        return { ...new_state };
    } else if (action.type === SET_PLANNING_RESULT_TYPE) {
        new_state.selected_planning_result_type = action.data;
        return { ...new_state };
    } else if (action.type === SET_LOCK_FILTER) {
        return { ...new_state, is_filter_locked: action.data };
    } else if (action.type === SET_SELECTED_FILTER_TYPE) {
        new_state = getInitialState(state);
        return { ...new_state, selected_filter_type: action.data };
    } else if (action.type === SET_SELECTED_ORDER_EXTERNAL_IDS) {
        new_state.selected_filter_type = TypeFilterValues.order;
        return { ...new_state, selected_order_external_ids: action.data };
    } else if (action.type === SET_SELECTED_STOCK_REQUIREMENT) {
        new_state = getInitialState(state);
        return { ...new_state };
    } else if (action.type === SET_SELECTED_CUSTOM_FILTER_CONFIG) {
        new_state = getInitialState(state);
        new_state.selected_custom_filter_entity = action.data.selected_custom_filter_entity;
        new_state.selected_custom_filter_field = action.data.selected_custom_filter_field;
        new_state.selected_custom_filter_input_type = action.data.selected_custom_filter_input_type;
        new_state.selected_custom_filter_match = action.data.selected_custom_filter_match;
        new_state.selected_filter_type = TypeFilterValues.custom;
        return { ...new_state };
    } else if (action.type === SET_SELECTED_CUSTOM_FILTER_VALUES) {
        new_state.selected_custom_filter_values = action.data;
        return { ...new_state };
    }

    return new_state;
};
